import React, { FC } from 'react';
import {
  usePriceFreezeInScope,
  usePriceFreezeSelection,
  withPrimeCheck,
  withUserLoggedCheck,
} from 'clientState/hooks/priceFreezeHooks';
import { PriceFreezePromotionalText } from '@components/Results/Components/Itinerary/PriceFreeze/PromotionalText/PriceFreezePromotionalText';
import {
  Body,
  Box,
  Card,
  SkeletonBox,
} from '@edreamsodigeo/prisma-design-system';
import { usePriceFreezeOfferQuery } from 'clientState/providers/PriceFreezeOfferProvider/useOffer';
import { Money } from 'generated/graphql';
import { createFeature } from '@edreamsodigeo/frontend-core';
import {
  INCREASE_RECOGNIZED_USERS_MOBILE,
  PRICE_FREEZE_PRIME_FIRST_PHASE_2_MOBILE,
} from 'clientState/ab/aliases';
import { PersuasivePill } from '@components/Results/Components/ThreeColumnsItinerary/PersuasiveTags/Pills';

export type PriceFreezePromoProps = {
  available: boolean | null;
  loading: boolean;
  deposit: Money | null;
  isPrimeUser: boolean;
  returnFormattedText?: boolean;
  shortMessage?: boolean;
  isUnavailableItinerary?: boolean;
};

export function PriceFreezePromoComponent({
  available,
  loading,
  deposit,
  isPrimeUser,
  returnFormattedText = true,
  shortMessage = false,
  isUnavailableItinerary,
}: PriceFreezePromoProps) {
  if (available === false) {
    return null;
  }

  if (loading) {
    return <PromotionalPillSkeleton />;
  }

  return returnFormattedText ? (
    <PersuasivePill
      type="informative"
      theme="soft"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      <Body type="small" weight="medium" color="primary.50">
        <PriceFreezePromotionalText
          deposit={deposit}
          isPrimeUser={isPrimeUser}
          shortMessage={shortMessage}
        />
      </Body>
    </PersuasivePill>
  ) : (
    <PersuasivePill
      type="informative"
      theme="soft"
      rounded
      isUnavailableItinerary={isUnavailableItinerary}
    >
      <PriceFreezePromotionalText
        deposit={deposit}
        isPrimeUser={isPrimeUser}
        shortMessage={shortMessage}
      />
    </PersuasivePill>
  );
}

const PriceFreezePromoLoggedNoPrime: FC<PriceFreezePromoProps> = createFeature({
  name: INCREASE_RECOGNIZED_USERS_MOBILE,
  variations: [
    withPrimeCheck(PriceFreezePromoComponent),
    withUserLoggedCheck(PriceFreezePromoComponent),
  ],
  version: 1,
});

export const PriceFreezePromo: FC<PriceFreezePromoProps> = createFeature({
  name: PRICE_FREEZE_PRIME_FIRST_PHASE_2_MOBILE,
  variations: [PriceFreezePromoLoggedNoPrime, PriceFreezePromoComponent],
  version: 1,
});

export function usePriceFreezePromo(): {
  available: boolean | null;
  loading: boolean;
  deposit: Money | null;
} {
  const { searchId, fareItineraryKey, segmentKeys } = usePriceFreezeSelection();
  const isInScope = usePriceFreezeInScope();
  const { loading, data } = usePriceFreezeOfferQuery({
    searchId,
    fareItineraryKey,
    segmentKeys,
    lazyCondition: !isInScope,
  });

  let available: boolean | null = null;
  let deposit = null;

  if (!isInScope) {
    available = false;
  }

  if (!loading && data && 'itineraryPriceFreezeOffer' in data) {
    deposit = data?.itineraryPriceFreezeOffer?.offer?.deposit;
    available = Boolean(data.itineraryPriceFreezeOffer) && Boolean(deposit);
  }

  return {
    available,
    loading,
    deposit,
  };
}

function PromotionalPillSkeleton() {
  return (
    <Box
      data-testid="promotional-skeleton"
      overflow={'hidden'}
      mb={'minus_1x_small'}
    >
      <Card>
        <SkeletonBox
          width="90px"
          height="24.5px"
          m="minus_base"
          borderRadius="12px"
        />
      </Card>
    </Box>
  );
}
